import { Injectable, inject } from '@angular/core';
import { ICustomWindow, WindowRefService } from './window-ref.service';

export interface ILinkTrackingData {
    linkId: string;
    linkUrl: string;
    linkText: string;
    linkClasses: string;
    outbound: string;
}

@Injectable({
    providedIn: 'root',
})
export class LinkService {
    private _windowRef = inject(WindowRefService);

    private get Window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    getTrackingData(el: HTMLElement): ILinkTrackingData {
        const linkId = el.getAttribute('id') ?? '';
        const linkHref = el.getAttribute('href') ?? '';
        const linkUrl = this.getFullPath(linkHref);
        const linkText = el.innerText;
        const linkClasses = el.classList.toString();
        const isBlank = el.getAttribute('target') === '_blank';
        const outbound = isBlank.toString().toUpperCase();

        return { linkId, linkUrl, linkText, linkClasses, outbound };
    }

    getSlug(url: string | undefined): string {
        const safeUrl = url ?? '';
        const path = safeUrl.endsWith('/') ? safeUrl.slice(0, -1) : safeUrl;
        const pathArr = path.split('/');

        return pathArr[pathArr.length - 1];
    }

    getFullPath(url: string | undefined): string {
        const safeUrl = url ?? '';
        return safeUrl.startsWith('/') ? location.origin + safeUrl : safeUrl;
    }

    isUrlAbsolute(url: string | null | undefined): boolean {
        return !!url && (url.indexOf('http://') === 0 || url.indexOf('https://') === 0);
    }

    /**
     * Returns `undefined` for empty/whitespace hrefs and the truthy literal
     * strings `'null'`/`'undefined'`, which a data source can emit instead of
     * omitting the property. A plain `!href` check does not catch these.
     */
    normalizeHref(url: string | undefined): string | undefined {
        const trimmed = url?.trim();
        if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
            return undefined;
        }

        return trimmed;
    }

    getUtmParams(): { name: string; value: string }[] | undefined {
        if (!this.Window) return undefined;

        const url = new URL(this.Window.location.href);
        const utm: { name: string; value: string }[] = [];
        const referrer = this.Window.document.referrer;

        // Strips values: www. | http://(www.) | https://(www.) | android-app://(www.)
        // from referrer and uses the remaining part of the URL before the first /
        const referrerDomain = referrer.replace(/^((https?|android-app):\/\/)?(?:www\.)?/i, '').split('/')[0];

        utm.push({ name: 'referrer', value: referrerDomain });

        url.searchParams.forEach((val, key) => {
            if (key.startsWith('utm_')) {
                utm.push({ name: key, value: val });
            }
        });

        return utm;
    }
}
